import React, { useEffect, useState } from "react";
import "../Styles/Permission.css";
import { toast, ToastContainer } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { FaPlus } from "react-icons/fa";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import SolarMenu from "../asset/svg/solarMenu";
import ReLoginModal from "../components/ReLoginModal";
import { SidebarModal } from "../components/Modals/sidebarModal";
import PermissionsForm from "../components/Forms/PermissionsForm";
import PermissionsEditForm from "../components/Forms/PermissionsEditingForm";
import RoleDeleteModal from "../components/RoleDeleteModal";
import api from "../util/axios";

type Role = {
  _id: string;
  role: string;
  createdAt: string;
  permissions: {
    [key: string]: {
      view: boolean;
      edit: boolean;
      add: boolean;
      delete: boolean;
    };
  };
};

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const Permissions = () => {
  const token = window.localStorage.getItem("token");
  const userRole = window.localStorage.getItem("userRole");
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [reLogin, setReLogin] = useState(false);
  const [createNew, setCreateNew] = useState(false);
  const [modalDisplay, setModalDisplay] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [selectedRoleId, setSelectedRoleId] = useState<string | "">("");

  const handleDeleteRole = async (id: string) => {
    setIsLoading(true);
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      return;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    try {
      await api.delete(
        `/roles/${id}`,
        { headers }
      );
      toast.success("Role deleted successfully");
      setModalOpen(false);
      fetchRoles();
      setIsLoading(false);
    } catch (error) {
      setModalOpen(false);
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to delete Role";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {

        setReLogin(true);
      } else {
        setReLogin(false);
      }
      toast.error(errorMessage);
      setIsLoading(false);
    }

  };

  const fetchRoles = () => {
    setIsLoading(true);
    if (!token) {
      setReLogin(true);
      setIsLoading(false);
      return;
    }
    api
      .get("/roles", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setRoles(response.data.data);
        setIsLoading(false);
      })
      .catch((error) => {
        const err = error as ApiError;
        const errorMessage =
          err.response?.data?.message || err.message || "Failed to fetch Roles";
        if (
          errorMessage.includes("jwt expired") ||
          errorMessage.includes("jwt malformed")
        ) {
          setReLogin(true);
        } else {
          setReLogin(false);
        }
        toast.error("Unable to fetch user roles");
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  return (
    <div className="branch">
      <ToastContainer />
      {reLogin ? <ReLoginModal /> : null}
      <h2>User roles and Permissions Settings</h2>
      <div className="permission">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h1>Loading...</h1>
            <h3 className="!text-gray-600 font-bold">
              Please wait while we process the request...
            </h3>
          </div>
        ) : (
          <>
            <button
              className="!my-6 flex gap-2 bg-blue-500 items-center p-2 text-white rounded-lg px-4 hover:text-gray-200"
              onClick={() => {
                setCreateNew(true);
                setModalDisplay(true);
              }}
            >
              Create new<FaPlus />
            </button>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white text-left text-sm text-gray-600">
                <thead className="bg-gray-100 text-xs uppercase tracking-wide text-gray-700">
                  <tr>
                    <th className="px-6 py-3">S/N</th>
                    <th className="px-6 py-3">Role</th>
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.length > 0 ? (
                    roles.map((role, index) => {
                      const formattedDate = new Date(
                        role.createdAt
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                      });
                      return (
                        <tr key={role._id}>
                          <td className="px-6 py-4">{index + 1}</td>
                          <td className="px-6 py-4">{role.role.toUpperCase()}</td>
                          <td className="px-6 py-4">{formattedDate}</td>
                          <td className="px-6 py-4">
                            <DropdownMenu.Root>
                              <DropdownMenu.Trigger asChild>
                                <button>
                                  <SolarMenu />
                                </button>
                              </DropdownMenu.Trigger>
                              <DropdownMenu.Portal>
                                <DropdownMenu.Content
                                  className="bg-white rounded shadow p-2"
                                  sideOffset={5}
                                >
                                  <DropdownMenu.Item
                                    className="px-2 text-blue-500 py-2 rounded hover:bg-gray-100 focus:outline-none cursor-pointer"
                                    onSelect={() => {
                                      setModalDisplay(true);
                                      setCreateNew(false);
                                      setSelectedRoleId(role._id);
                                    }}
                                  >
                                    View / Edit
                                  </DropdownMenu.Item>
                                  {userRole === "admin" ? (
                                    <DropdownMenu.Item
                                      className="px-2 py-2 rounded hover:bg-red-100 text-red-600 focus:outline-none cursor-pointer"
                                      onSelect={() => {
                                        setSelectedRoleId(role._id);
                                        setModalOpen(true);
                                      }}
                                    >
                                      Delete
                                    </DropdownMenu.Item>
                                  ) : null}
                                </DropdownMenu.Content>
                              </DropdownMenu.Portal>
                            </DropdownMenu.Root>
                            <RoleDeleteModal
                              isOpen={modalOpen}
                              onClose={() => {
                                setModalOpen(false);
                              }}
                              onConfirm={() => {
                                if (selectedRoleId) handleDeleteRole(selectedRoleId);
                              }}
                              roleId={selectedRoleId || ""}
                            />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center" }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <SidebarModal
              title={createNew ? "Create New User Role" : "View / Edit"}
              display={modalDisplay}
              close={() => {
                setModalDisplay(false);
              }}
            >
              {createNew ? (
                <PermissionsForm
                  modalDisplay={modalDisplay}
                  createNew={createNew}
                  onClose={() => setModalDisplay(false)}
                  onRoleCreated={fetchRoles}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                />
              ) : (
                <PermissionsEditForm
                  modalDisplay={modalDisplay}
                  onClose={() => setModalDisplay(false)}
                  onRoleEdited={fetchRoles}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  roleData={roles.find((role) => role._id === selectedRoleId) || { role: "", permissions: {}, _id: "", createdAt: "" }}
                />
              )}
            </SidebarModal>
          </>
        )}
      </div>
    </div>
  );
};

export default Permissions;